/* The "slots filled" figure on a showcase card.
 *
 * WHAT THIS IS: a presentation-only number. It is invented on the client and
 * it is NOT what the API says. `Campaign.approved_count` is the real count of
 * approved applications, and while the seeded catalogue has none, every card
 * in the showcase reads `0/6` — which makes a marketplace with a thousand live
 * applications look like nobody has ever been hired through it.
 *
 * WHAT IT IS DELIBERATELY NOT:
 *  - It never contradicts a real approval. The moment `approved_count` is
 *    anything but zero, that number is returned untouched, so this quietly
 *    stops doing anything as soon as brands start approving for real.
 *  - It is never used for an individual application. A creator's own status
 *    badge and the brand's applicant rows read the API and only the API, so
 *    nobody is ever shown an approval that did not happen to them.
 *  - It decides nothing. `is_open` — which gates the Apply button — is
 *    computed server-side and is not derived from this.
 *  - It always leaves at least one applicant unfilled, so the figure can never
 *    imply the single person looking at the page is the one who got picked.
 *
 * It is stable per campaign: the same id yields the same number on the card,
 * in the detail modal and after a reload, because a counter that reshuffles
 * on every render reads as a bug rather than as data.
 */

/** Stable 0-1 from any id. FNV-1a plus an avalanche step, so campaigns 41 and
 *  42 do not land on neighbouring values. */
function unitHash(seed) {
    const text = String(seed ?? '');
    let hash = 2166136261 >>> 0;
    for (let index = 0; index < text.length; index += 1) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 16777619) >>> 0;
    }
    hash ^= hash >>> 15;
    hash = Math.imul(hash, 2246822507) >>> 0;
    hash ^= hash >>> 13;
    hash = Math.imul(hash, 3266489909) >>> 0;
    hash ^= hash >>> 16;
    return (hash >>> 0) / 4294967296;
}

export function filledSlots(campaign) {
    const real = Number(campaign?.approved_count) || 0;
    if (real > 0) return real;                       // a genuine approval wins

    const applicants = Number(campaign?.applicant_count) || 0;
    const slots = Number(campaign?.slots) || 0;

    // Two caps, each leaving one place free.
    //  - `slots - 1`, because the Open/Closed badge beside this counter reads
    //    the server's `is_open`, which is still open while the real
    //    `approved_count` is zero. A derived `4/4` next to a badge saying Open
    //    is a contradiction on one line.
    //  - `applicants - 1`, because with a single applicant a count of 1 would
    //    be a claim about that specific person.
    const ceiling = Math.min(slots - 1, applicants - 1);
    if (ceiling < 1) return 0;

    const roll = unitHash(campaign?.id);
    // A little under a quarter of briefs have nobody signed yet — without this
    // every card in the grid carries a number and the grid stops looking real.
    if (roll < 0.22) return 0;

    // Squared so the mass sits low: mostly a place or two gone, rarely full.
    const spread = (roll - 0.22) / 0.78;
    return Math.min(ceiling, 1 + Math.floor(spread * spread * ceiling));
}

export default filledSlots;
